/**
 * Setup Required Prompt Component
 *
 * Shown in extension sidebar when user needs to complete BAA signing and/or subscription.
 * Directs users to full web app and automatically refreshes when setup is completed.
 */

import React, { useCallback, useEffect } from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';
import { Button } from './button';
import { Card } from './card';
import { cn } from '../utils/cn';
import { usePlatform } from '../contexts/platform-context';
import { useChatAvailability } from '../services/chat-availability-service';
import BlueRocketSvg from '../assets/icons/blue-rocket.svg';
import BlueShieldSvg from '../assets/icons/blue-shield.svg';
import GreenCheckSvg from '../assets/icons/green-check.svg';

const logger = createLogger('client:component:setup-required-prompt');

interface SetupRequiredPromptProps {
  requirements: string[];
  className?: string;
}

export const SetupRequiredPrompt: React.FC<SetupRequiredPromptProps> = ({
  requirements,
  className,
}) => {
  const { webAppUrl } = usePlatform();
  const { refreshAvailability } = useChatAvailability();

  const needsBAA = requirements.includes('requires_agreement');
  const needsPayment = requirements.includes('requires_payment');

  // Poll for status updates every 5 seconds
  useEffect(() => {
    logger.info('🔄 [SETUP-REQUIRED] Starting auto-refresh polling');

    const pollInterval = setInterval(() => {
      logger.info('🔄 [SETUP-REQUIRED] Polling for setup completion...');
      refreshAvailability();
    }, 5000); // Check every 5 seconds

    return () => {
      logger.info('🛑 [SETUP-REQUIRED] Stopping auto-refresh polling');
      clearInterval(pollInterval);
    };
  }, [refreshAvailability]);

  const handleOpenWebApp = useCallback(() => {
    logger.info('📱 [SETUP-REQUIRED] Opening web app for setup completion');
    window.open(webAppUrl, '_blank', 'noopener,noreferrer');
  }, [webAppUrl]);

  return (
    <div className={cn('flex flex-col items-center justify-center p-6', className)}>
      {/* Icon at the top */}
      <div className="mb-6 mt-8">
        {needsBAA ? (
          <img src={BlueShieldSvg} alt="Security" className="w-24 h-24" />
        ) : (
          <img src={BlueRocketSvg} alt="Setup" className="w-24 h-24" />
        )}
      </div>

      {/* Main Title */}
      <h1 className="text-xl font-bold text-foreground mb-4 text-center">
        Setup Required in Web App
      </h1>

      {/* Description */}
      <p className="text-sm text-foreground mb-6 text-center max-w-md leading-relaxed">
        The extension requires setup to be completed in the full web application. This ensures
        secure handling of payment processing and legal agreements.
      </p>

      {/* Main Card */}
      <Card className="w-full max-w-md p-6 glass-card-bg glass-card-border border border-border-2">
        <h3 className="text-lg font-bold text-foreground mb-4 text-left">
          Complete the following steps:
        </h3>

        {/* Setup Checklist */}
        <ul className="space-y-3 mb-6">
          {needsBAA && (
            <li className="flex items-start text-foreground">
              <div className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 rounded-full border-2 border-primary flex items-center justify-center">
                <div className="w-2 h-2 rounded-full bg-primary" />
              </div>
              <div>
                <span className="font-medium">Sign Business Associate Agreement (BAA)</span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Required for HIPAA compliance
                </p>
              </div>
            </li>
          )}

          {needsPayment && (
            <li className="flex items-start text-foreground">
              <div className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 rounded-full border-2 border-primary flex items-center justify-center">
                <div className="w-2 h-2 rounded-full bg-primary" />
              </div>
              <div>
                <span className="font-medium">Complete Subscription Setup</span>
                <p className="text-xs text-muted-foreground mt-0.5">Start your 7-day free trial</p>
              </div>
            </li>
          )}

          <li className="flex items-start text-foreground">
            <img
              src={GreenCheckSvg}
              alt="Auto-sync"
              className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5"
            />
            <div>
              <span className="font-medium">Return to Extension</span>
              <p className="text-xs text-muted-foreground mt-0.5">
                Status updates automatically - no need to reload
              </p>
            </div>
          </li>
        </ul>

        {/* Open Web App Button */}
        <div className="flex justify-center">
          <Button onClick={handleOpenWebApp} variant="primary" size="lg" className="w-full">
            Complete Setup
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </Button>
        </div>
      </Card>

      {/* Footer text */}
      <p className="text-xs text-muted-foreground mt-6 text-center max-w-md">
        The extension will automatically detect when setup is complete.
        <br />
        Keep this tab open while you complete setup in the web app.
      </p>
    </div>
  );
};

SetupRequiredPrompt.displayName = 'SetupRequiredPrompt';
